import { useState, useRef, useEffect } from 'react';

// Singleton for global state
const globalAudioState = {
  audioInstance: new Audio(),
  currentPlayingId: null,
};

const useAudioPlayer = (id) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInterrupted, setIsInterrupted] = useState(false);

  const audioRef = useRef(globalAudioState.audioInstance);

  const resetAudioState = () => {
    setIsLoading(false);
    setIsPlaying(false);
    setIsInterrupted(false);
  };

  const playAudio = async (text) => {
    const audio = audioRef.current;

    // Stop currently playing audio if it's a different ID
    if (globalAudioState.currentPlayingId && globalAudioState.currentPlayingId !== id) {
      console.log(`Stopping audio for message ID: ${globalAudioState.currentPlayingId}`);
      audio.pause();
      audio.currentTime = 0;
      globalAudioState.currentPlayingId = null;
      audio.src = ''; // Ensure previous audio is cleared
    }

    setIsLoading(true); // Set loading state before starting fetch

    try {
      const response = await fetch('https://dewa-uaen-prd-rammas-appsvc-bot-fva0avabh8hkana5.uaenorth-01.azurewebsites.net/api/tts', {
        method: 'POST',
        body: JSON.stringify({ text }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audio');
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);

      // Stop current audio before playing new one
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }

      // Assign new audio source and start playback
      audio.src = audioUrl;
      globalAudioState.currentPlayingId = id;

      audio.oncanplaythrough = () => {
        if (globalAudioState.currentPlayingId === id) {
          setIsLoading(false); // Loading complete
          audio.play();
        }
      };

      audio.onplay = () => {
        setIsPlaying(true);
        setIsInterrupted(false);
        console.log(`Audio started playing for message ID: ${id}`);
      };

      audio.onpause = () => {
        setIsPlaying(false);
        setIsInterrupted(true);
        console.log(`Audio interrupted for message ID: ${id}`);
      };

      audio.onended = () => {
        setIsPlaying(false);
        setIsInterrupted(false);
        console.log(`Audio finished for message ID: ${id}`);
        URL.revokeObjectURL(audioUrl);
        globalAudioState.currentPlayingId = null;
      };

      audio.onerror = (e) => {
        console.error(`Audio playback error for message ID: ${id}:`, e);
        resetAudioState();
        URL.revokeObjectURL(audioUrl);
        globalAudioState.currentPlayingId = null;
      };
    } catch (error) {
      console.error('Error playing audio:', error);
      resetAudioState();
      globalAudioState.currentPlayingId = null;
    }
  };

  const stopAudio = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      resetAudioState();
      console.log(`Audio stopped for message ID: ${id}`);
      if (globalAudioState.currentPlayingId === id) {
        globalAudioState.currentPlayingId = null;
      }
      audio.src = ''; // Clear the audio source to ensure proper cleanup
    }
  };

  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio && globalAudioState.currentPlayingId === id) {
        audio.pause();
        audio.src = '';
        console.log(`Audio cleanup for message ID: ${id}`);
        globalAudioState.currentPlayingId = null;
      }
    };
  }, [id]);

  return [isLoading, isPlaying, isInterrupted, playAudio, stopAudio];
};

export default useAudioPlayer;

